import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { getLocalDateForMidnightUTC } from 'utils';
import * as yup from 'yup';

export const fields = [
  'claimsHandler',
  'dateReported',
  'status',
  'dateOfLoss',
  'catCode',
  'causeOfLoss',
  'currency',
  'lossLocation',
  'city',
  'country',
  'lossDescription',
] as const;

export interface ContractDateRange {
  inceptionDate: Date;
  expiryDate: Date;
}

export const getValidationSchema = (
  t: TFunction,
  contractDateRanges?: ContractDateRange[]
) =>
  yup.object().shape({
    claimsHandler: yup
      .string()
      .required(t('validation.required', { field: t('claim.claimsHandler') })),
    dateReported: yup
      .date()
      .required(t('validation.required', { field: t('claim.dateReported') }))
      .test(
        'is-after-date-of-loss',
        t('claim.reportedDateMustBeAfterDateOfLoss'),
        function (value) {
          const dateOfLoss = this.parent.dateOfLoss;
          if (!value || !dateOfLoss) return true;

          const reportedDate = dayjs(value);
          const lossDate = dayjs(dateOfLoss);

          return reportedDate.isSameOrAfter(lossDate, 'day');
        }
      ),
    dateOfLoss: yup
      .date()
      .required(t('validation.required', { field: t('claim.dateOfLoss') }))
      .test(
        'is-within-contract-dates',
        t('claim.dateOfLossMustBeWithinContractDates'),
        function (value) {
          if (!value || !contractDateRanges || contractDateRanges.length === 0)
            return true;

          const lossDate = dayjs(value);

          const isWithinAnyContract = contractDateRanges.some((range) => {
            const inception = getLocalDateForMidnightUTC(range.inceptionDate);
            const expiry = getLocalDateForMidnightUTC(range.expiryDate);

            return (
              lossDate.isSameOrAfter(inception, 'day') &&
              lossDate.isSameOrBefore(expiry, 'day')
            );
          });

          return isWithinAnyContract;
        }
      ),
    catCode: yup
      .string()
      .required(t('validation.required', { field: t('claim.selectCatCode') })),
    causeOfLoss: yup
      .string()
      .required(t('validation.required', { field: t('claim.causeOfLoss') })),
    currency: yup
      .string()
      .required(t('validation.required', { field: t('claim.currency') })),
    city: yup
      .string()
      .required(t('validation.required', { field: t('general.city') })),
    country: yup
      .string()
      .required(t('validation.required', { field: t('general.country') })),
    lossLocation: yup.string().nullable(),
    lossDescription: yup.string().nullable(),
  });
